/**
 * Durability helpers for bulk file uploads (file.ts).
 *
 * A user who drops thousands of files and walks away needs the batch to survive
 * three separate hazards, and each one needs a different mechanism:
 *
 *   1. a transient network failure mid-batch  -> retry with backoff
 *   2. the tab being frozen/discarded while hidden -> a held Web Lock
 *   3. a reload, a crash, or the tab being closed -> the queue persisted to
 *      IndexedDB and offered back on next load
 *
 * Only (3) is a real guarantee. (2) is a strong hint to the browser that this
 * page is doing work, not a contract — no API can stop an OS-level kill — which
 * is exactly why (3) has to exist underneath it.
 *
 * Everything here is best-effort by construction: a browser with no IndexedDB, a
 * private window, a storage quota refusal or a missing Web Locks API must all
 * degrade to "uploads still work, resume is just unavailable". Persistence must
 * never be able to fail an upload.
 */

// ─── retry ───────────────────────────────────────────────────────────────────

/** Attempts per file, including the first. */
export const UPLOAD_MAX_ATTEMPTS = 4;
const RETRY_BASE_MS = 1000;
const RETRY_MAX_MS = 15000;

/**
 * Whether a failed upload attempt is worth repeating.
 *
 * Deliberately an ALLOW-list. skapi's transport rejects with bare strings
 * ('Network error' / 'Timeout' / 'Too many requests' — utils/network.ts:850-852)
 * and a non-2xx rejects with the parsed body, so anything we do not recognise as
 * a transport failure is treated as terminal and fails the file immediately,
 * which is the pre-retry behaviour. That keeps a misclassified permanent error
 * (a rejected file type, a bad path, a quota refusal) from being hammered four
 * times, and it keeps EXISTS — the overwrite probe's designed outcome — out of
 * the retry path entirely.
 *
 * 'Aborted' is excluded on purpose: it is what a user-cancelled upload rejects
 * with, and retrying it would fight the user.
 */
export function isRetriableUploadError(err: any): boolean {
    if (err == null) return false;
    const text = typeof err === "string" ? err : (err.message || err.body?.message || "");
    if (typeof text === "string") {
        const t = text.toLowerCase();
        if (t.includes("network error") || t.includes("timeout") || t.includes("too many requests")) return true;
        if (t.includes("failed to fetch") || t.includes("load failed")) return true; // fetch()'s transport failure
    }
    // An explicit server-side 5xx. 4xx is the caller's fault and repeating it
    // just repeats the same answer.
    const status = Number(err.status ?? err.status_code ?? err.body?.status_code);
    if (Number.isFinite(status) && status >= 500 && status < 600) return true;
    return false;
}

/** Exponential backoff with jitter, so a batch that trips a server-side limit
 *  does not march all its workers back in lockstep. */
export function uploadRetryDelayMs(attempt: number): number {
    const base = Math.min(RETRY_BASE_MS * Math.pow(2, Math.max(0, attempt - 1)), RETRY_MAX_MS);
    return Math.round(base * (0.5 + Math.random() * 0.5));
}

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ─── persisted queue (IndexedDB) ─────────────────────────────────────────────

const DB_NAME = "bq-uploads";
const DB_VERSION = 1;
const STORE = "pending";
/** Persisted in chunks rather than one transaction: a 10k-file drop in a single
 *  transaction is a long-running lock that can be aborted wholesale, and a
 *  partial persist is far better than none. */
const PERSIST_CHUNK = 250;

/**
 * Bytes per transaction, alongside the row count above.
 *
 * A row carries the FILE. Chunking by rows alone says nothing about size, so 250
 * rows is 250 KB of text files or 5 GB of scans, and the browser has to buffer
 * whichever it is: an IndexedDB write goes through the browser process, not the
 * renderer, so a multi-gigabyte transaction puts the pressure somewhere a tab
 * crash cannot contain. That is the shape of the "browser shuts down during a
 * big upload" reports, and it is why this exists.
 *
 * A chunk is now whichever limit is reached first. A single file larger than
 * this still gets a transaction of its own rather than being dropped.
 */
const PERSIST_CHUNK_BYTES = 16 * 1024 * 1024;

/**
 * Biggest file whose bytes are worth mirroring.
 *
 * Copying half a gigabyte into IndexedDB to save one drag-and-drop is a bad
 * trade, and it is the same copy that makes the write dangerous. Files past this
 * still upload normally; they are simply not resumable, and they are recorded as
 * untracked so the user is told rather than left to assume.
 */
export const PERSIST_MAX_FILE_BYTES = 128 * 1024 * 1024;

/**
 * Ceiling on the bytes ONE batch mirrors.
 *
 * Mirroring is a copy: a 20 GB folder drop asks the browser to write 20 GB to
 * disk while it uploads the same 20 GB. Past this the remaining files upload
 * without being resumable.
 */
export const PERSIST_MAX_TOTAL_BYTES = 2 * 1024 * 1024 * 1024;

/** How much of the origin's REMAINING quota one batch may spend. Never fill the
 *  disk to make an upload resumable: the upload matters more than the resume. */
const PERSIST_FREE_FRACTION = 0.25;

/**
 * What this batch may mirror, in bytes.
 *
 * The fixed ceiling, lowered to fit what the origin actually has left. Without
 * the estimate the old code would happily attempt a batch far larger than the
 * quota and learn about it one failed transaction at a time.
 */
async function persistBudgetBytes(): Promise<number> {
    let budget = PERSIST_MAX_TOTAL_BYTES;
    try {
        const est: any = await (navigator as any)?.storage?.estimate?.();
        const quota = Number(est?.quota);
        const usage = Number(est?.usage);
        if (Number.isFinite(quota) && Number.isFinite(usage)) {
            budget = Math.min(budget, Math.max(0, Math.floor((quota - usage) * PERSIST_FREE_FRACTION)));
        }
    } catch (e) {
        // No estimate API, or it refused. The fixed ceiling still applies.
    }
    return budget;
}

/**
 * Where a row is in the file's lifecycle.
 *
 * A row used to cover the BYTES only, and was deleted the moment they landed.
 * But storing a file is not the end of the work: the caller then has to create
 * its index record and get an indexing request accepted, and a tab that dies in
 * that window left a file that was uploaded, never indexed, and (because the
 * index record had already been written) painted as "Indexed" by the badge. So
 * the row now outlives the upload and is deleted only once the whole handover
 * is done.
 *
 *   'upload'  bytes not stored yet. Resumable, carries the blob.
 *   'index'   bytes stored, indexing not yet dispatched. The blob is dropped -
 *             it is on the server, and keeping thousands of them is what would
 *             actually exhaust the quota. Repaired without re-uploading.
 *   'failed'  the upload itself failed terminally. Keeps the blob so a retry
 *             needs nothing from the user, and is the durable record of a
 *             failure that used to live only in a module-local array.
 */
export type UploadPhase = "upload" | "index" | "failed";

export type PersistedUploadError = { code: string; message: string };

export type PersistedUpload = {
    /** Stable per destination path, so re-adding the same file replaces its row
     *  instead of stacking duplicates. */
    id: string;
    serviceId: string;
    target: "db" | "host";
    storeKey?: string;
    currentDir: string;
    /** Stored explicitly: `webkitRelativePath` does NOT survive a structured
     *  clone, so a resumed File cannot reconstruct its own folder path. */
    relPath: string;
    name: string;
    /** Stored alongside the blob so the File can be rebuilt on resume without
     *  trusting the round trip to have preserved the wrapper (see
     *  rehydratePersistedFile). */
    type: string;
    size: number;
    lastModified: number;
    /** Absent once the bytes are stored (phase 'index'): nothing downstream
     *  needs them, and dropping them is what keeps a 10k batch's leftovers
     *  small enough to survive in storage. */
    file?: File;
    addedAt: number;
    /** Absent on rows written before this existed, which are all mid-upload by
     *  construction, so absence reads as 'upload'. */
    phase?: UploadPhase;
    /** Why the upload failed ('failed'), or why the last index-setup attempt
     *  failed ('index'). Shown to the user, so it holds the server's own words. */
    error?: PersistedUploadError;
    /** Index-setup attempts already spent, so a file that can never be
     *  dispatched is reported instead of retried on every visit forever. */
    indexAttempts?: number;
    /** When the bytes landed. Only for ordering the report. */
    settledAt?: number;
    /** The upload this row covers REPLACED an existing file (the server answered
     *  EXISTS and the user chose Overwrite), or was an explicit Reindex. The
     *  stale `src::` index has to be deleted before the file is indexed again,
     *  or the old version's extracted rows survive alongside the new ones.
     *  Captured at handover because a repair after a reload cannot recover it:
     *  the folder cache it was derived from is gone. */
    replacesIndex?: boolean;
    /** When some tab took the index-setup lease on this row. See
     *  leaseIndexSetup. */
    setupLeaseAt?: number;
};

/** Rows written before phases existed predate the index-setup guarantee, and a
 *  row only ever existed mid-upload back then. */
export const phaseOf = (row: PersistedUpload): UploadPhase => row.phase || "upload";

/** Attempts to get one file's indexing dispatched, across visits. Past this the
 *  file is reported rather than retried: something about it is not transient. */
export const INDEX_SETUP_MAX_ATTEMPTS = 3;

/**
 * Return a real File for a recovered row.
 *
 * A File is defined as serializable, so a browser's IndexedDB hands back a File
 * with its name intact. Not every engine does: Node's own structuredClone (and
 * therefore several test and polyfill stacks) drops the wrapper and returns a
 * bare Blob. That difference is not cosmetic here — `FormData.append` names a
 * nameless Blob "blob", so a resumed batch would upload every file under that
 * one name, and the mime lookup, which reads the extension off the name, would
 * fail with it. The name/type were persisted next to the bytes precisely so this
 * can be rebuilt instead of assumed. Rebuilding references the same bytes; it
 * does not copy them.
 */
export function rehydratePersistedFile(row: PersistedUpload): File | null {
    const f: any = row.file;
    // A row past the upload phase has had its blob dropped on purpose. Returning
    // an empty File here would be worse than returning nothing: the caller would
    // upload zero bytes over the real file under the same deterministic key.
    if (f == null) return null;
    if (typeof File !== "undefined" && f instanceof File && f.name) return f;
    return new File([f], row.name, {
        type: row.type || f?.type || "",
        lastModified: row.lastModified || row.addedAt || Date.now(),
    });
}

export const persistedUploadId = (target: string, storeKey: string | undefined, storagePath: string) =>
    `${target}|${storeKey || ""}|${storagePath}`;

let dbPromise: Promise<IDBDatabase | null> | null = null;
/** Flipped by any failure. Resume is then reported as unavailable rather than
 *  silently promising a recovery that cannot happen. */
let persistenceBroken = false;

function openDb(): Promise<IDBDatabase | null> {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise<IDBDatabase | null>((resolve) => {
        try {
            if (typeof indexedDB === "undefined") { resolve(null); return; }
            const req = indexedDB.open(DB_NAME, DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(STORE)) {
                    const os = db.createObjectStore(STORE, { keyPath: "id" });
                    os.createIndex("serviceId", "serviceId", { unique: false });
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => { persistenceBroken = true; resolve(null); };
            req.onblocked = () => resolve(null);
        } catch (e) {
            persistenceBroken = true;
            resolve(null);
        }
    });
    return dbPromise;
}

/** Ask the browser not to evict this origin's storage under pressure. Best
 *  effort and non-blocking: the answer is advisory and often "no". */
export function requestPersistentStorage(): void {
    try {
        (navigator as any)?.storage?.persist?.().catch(() => {});
    } catch (e) { /* not supported */ }
}

export function isResumeAvailable(): boolean {
    return !persistenceBroken;
}

// ─── untracked files ─────────────────────────────────────────────────────────
/**
 * Files the browser refused to remember.
 *
 * Deliberately in localStorage rather than in the IndexedDB store: the thing
 * being recorded IS an IndexedDB failure, and writing the record of a failure
 * through the mechanism that just failed is circular. It is a handful of names
 * and a count, so it costs nothing where a blob would not fit.
 *
 * Without this the resume prompt counts only the rows that survived and states
 * that number as fact, so a user who lost 20 files to a quota refusal is told
 * 40 were interrupted, resumes all 40, and never learns about the rest.
 */
const UNTRACKED_KEY = "bq-untracked-uploads";
/** Enough to show the user what went missing without unbounded growth. */
const UNTRACKED_NAMES_KEPT = 50;

export type UntrackedUploads = { count: number; names: string[] };

const untrackedKeyFor = (serviceId: string) => UNTRACKED_KEY + ":" + serviceId;

export function recordUntrackedUploads(serviceId: string, rows: PersistedUpload[]): void {
    if (!serviceId || !rows.length) return;
    try {
        const prev = readUntrackedUploads(serviceId);
        const names = prev.names.slice();
        for (const r of rows) {
            if (names.length >= UNTRACKED_NAMES_KEPT) break;
            names.push(r.currentDir + r.relPath);
        }
        localStorage.setItem(
            untrackedKeyFor(serviceId),
            JSON.stringify({ count: prev.count + rows.length, names }),
        );
    } catch (e) { /* the warning is best-effort; never fail an upload over it */ }
}

export function readUntrackedUploads(serviceId: string): UntrackedUploads {
    try {
        const raw = localStorage.getItem(untrackedKeyFor(serviceId));
        if (!raw) return { count: 0, names: [] };
        const parsed = JSON.parse(raw);
        const count = Number(parsed?.count);
        return {
            count: Number.isFinite(count) && count > 0 ? count : 0,
            names: Array.isArray(parsed?.names) ? parsed.names.filter((n: any) => typeof n === "string") : [],
        };
    } catch (e) {
        return { count: 0, names: [] };
    }
}

export function clearUntrackedUploads(serviceId: string): void {
    try { localStorage.removeItem(untrackedKeyFor(serviceId)); } catch (e) { /* nothing to do */ }
}

export async function persistPendingUploads(
    items: PersistedUpload[],
    opts?: {
        /**
         * Re-checked immediately before each transaction, so a file that finished
         * while this was still deciding is not written back.
         *
         * The mirror is deliberately not awaited by its caller (the upload starts
         * now, and persistence must never delay it), which means a small file can
         * upload and have its row deleted before the row is even written. Writing
         * it then leaves an 'upload' row for a file that is already stored, and the
         * next visit offers to upload it again: a second copy and, worse, a second
         * index request for one document.
         */
        stillPending?: (row: PersistedUpload) => boolean;
    },
): Promise<void> {
    if (!items.length) return;
    const db = await openDb();
    if (!db) {
        // No store at all. Every one of these is untracked, and saying so is the
        // whole point: silence here is what made a partial recovery look total.
        recordUntrackedUploads(items[0]?.serviceId || "", items);
        return;
    }
    // What we can afford to copy. Rows are mirrored in queue order, so the budget
    // is spent on the files that are about to upload rather than on whichever
    // happened to be biggest.
    let budget = await persistBudgetBytes();
    const affordable: PersistedUpload[] = [];
    const unaffordable: PersistedUpload[] = [];
    let tooBig = 0;
    let outOfBudget = 0;
    for (const it of items) {
        // A row with no blob is metadata only (an index-phase row): it costs
        // nothing to keep and it is the record that a stored file still needs
        // indexing, so it is never subject to the budget.
        if (!it.file) { affordable.push(it); continue; }
        const bytes = Number(it.size) || 0;
        if (bytes > PERSIST_MAX_FILE_BYTES) { tooBig++; unaffordable.push(it); continue; }
        if (bytes > budget) { outOfBudget++; unaffordable.push(it); continue; }
        budget -= bytes;
        affordable.push(it);
    }
    if (unaffordable.length) {
        // Same admission a quota refusal makes: these files upload, they just
        // cannot be offered back. Saying so is the whole point of that record.
        recordUntrackedUploads(unaffordable[0]?.serviceId || "", unaffordable);
        // The two reasons are worth telling apart when reading a report: one is a
        // property of the file, the other of the machine it is being uploaded from.
        console.warn(
            `[upload] ${unaffordable.length} file(s) will upload but cannot be offered back if this tab dies ` +
            `(${tooBig} over the per-file mirror limit, ${outOfBudget} past what this browser can spare)`,
        );
    }

    let failedChunks = 0;
    let i = 0;
    while (i < affordable.length) {
        // Whichever limit comes first: rows, or bytes. The `chunk.length === 0`
        // term is what keeps a single large file from being skipped forever by a
        // byte cap it cannot fit under.
        const chunk: PersistedUpload[] = [];
        let chunkBytes = 0;
        while (i < affordable.length && chunk.length < PERSIST_CHUNK) {
            const next = Number(affordable[i].size) || 0;
            if (chunk.length > 0 && chunkBytes + next > PERSIST_CHUNK_BYTES) break;
            chunkBytes += next;
            chunk.push(affordable[i]);
            i++;
        }
        // Last look before the write. Cheap, and the only moment at which the
        // answer is still worth anything.
        const live = opts?.stillPending ? chunk.filter((it) => opts.stillPending!(it)) : chunk;
        if (!live.length) continue;

        try {
            await new Promise<void>((resolve, reject) => {
                const tx = db.transaction(STORE, "readwrite");
                const os = tx.objectStore(STORE);
                for (const it of live) os.put(it);
                tx.oncomplete = () => resolve();
                tx.onerror = () => reject(tx.error);
                tx.onabort = () => reject(tx.error);
            });
        } catch (e) {
            // KEEP GOING. This used to return, which was the worst possible
            // response to a quota refusal: rows are persisted in queue order and
            // consumed in queue order, so abandoning the rest mirrored exactly
            // the PREFIX that was about to upload anyway and dropped the TAIL
            // that would still be waiting when the tab died. One transaction
            // failing also does not mean the next will (chunks differ in size,
            // and eviction can free room mid-batch), so the remaining chunks are
            // still worth attempting.
            failedChunks++;
            persistenceBroken = true;
            // Name them, so the resume prompt can admit what it does not know
            // instead of stating a confident, wrong-low count.
            recordUntrackedUploads(chunk[0]?.serviceId || "", chunk);
        }
    }
    if (failedChunks) {
        console.warn(
            `[upload] ${failedChunks} chunk(s) of the pending-upload queue could not be persisted; ` +
            `those files cannot be offered back if this tab dies`,
        );
    }
}

/**
 * Merge a patch into one row, leaving the rest of it untouched.
 *
 * Read-modify-write inside a SINGLE transaction, so a concurrent update cannot
 * land between the read and the write. A missing row is not an error: it means
 * the file finished and was unpersisted while this was queued, and re-creating
 * it from a stale patch would resurrect work that is already done.
 */
export async function updatePersistedUpload(
    id: string,
    patch: Partial<PersistedUpload>,
    opts?: {
        dropFile?: boolean;
        /**
         * Write this row (patched) when there is nothing to update.
         *
         * For the handover out of the upload phase, and only for it. A file whose
         * bytes were too large to mirror has no row at all, and without this it
         * would reach the index phase unrecorded: stored, not yet indexed, and
         * invisible to the repair that exists precisely for that window. The row
         * it creates is metadata only, so it costs nothing and is not subject to
         * the mirror budget.
         *
         * Deliberately opt-in. Everywhere else a missing row means the file
         * finished and was unpersisted while this patch was queued, and
         * re-creating it there would resurrect work that is already done.
         */
        createFrom?: PersistedUpload;
    },
): Promise<boolean> {
    if (!id) return false;
    const db = await openDb();
    if (!db) return false;
    try {
        return await new Promise<boolean>((resolve, reject) => {
            const tx = db.transaction(STORE, "readwrite");
            const os = tx.objectStore(STORE);
            const req = os.get(id);
            let found = false;
            req.onsuccess = () => {
                const row = (req.result as PersistedUpload | undefined) || opts?.createFrom;
                if (!row) return; // gone already; tx completes with found = false
                found = true;
                const next: any = Object.assign({}, row, patch);
                if (opts?.dropFile) delete next.file;
                os.put(next);
            };
            tx.oncomplete = () => resolve(found);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } catch (e) {
        return false;
    }
}

export async function unpersistPendingUpload(id: string): Promise<void> {
    const db = await openDb();
    if (!db) return;
    try {
        await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE, "readwrite");
            tx.objectStore(STORE).delete(id);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } catch (e) { /* a stale row costs one skipped resume, not correctness */ }
}

export async function listPendingUploads(serviceId: string): Promise<PersistedUpload[]> {
    const db = await openDb();
    if (!db) return [];
    try {
        return await new Promise<PersistedUpload[]>((resolve, reject) => {
            const tx = db.transaction(STORE, "readonly");
            const idx = tx.objectStore(STORE).index("serviceId");
            const req = idx.getAll(serviceId);
            req.onsuccess = () => resolve((req.result || []) as PersistedUpload[]);
            req.onerror = () => reject(req.error);
        });
    } catch (e) {
        return [];
    }
}

/** Drop exactly these rows. What a "discard" should almost always use: the ids
 *  the user was actually shown, rather than everything the service happens to
 *  hold at that moment. */
export async function unpersistPendingUploads(ids: string[]): Promise<void> {
    for (const id of ids) await unpersistPendingUpload(id);
}

/**
 * Drop every row for a service.
 *
 * `exclude` is not optional in practice: the upload queue is a module singleton
 * that keeps running across route changes and across tabs, so "everything for
 * this service" reliably includes rows belonging to a batch that is uploading
 * right now. Wiping those does not stop the batch, it silently removes its
 * ability to survive a crash, which is the failure this whole module exists to
 * prevent.
 */
export async function clearPendingUploads(
    serviceId: string,
    opts?: { exclude?: Set<string> },
): Promise<void> {
    const rows = await listPendingUploads(serviceId);
    for (const r of rows) {
        if (opts?.exclude?.has(r.id)) continue;
        await unpersistPendingUpload(r.id);
    }
}

// ─── index-setup handover ────────────────────────────────────────────────────

/**
 * Files whose index setup is running RIGHT NOW, keyed by persisted-row id.
 *
 * Module scope, not component scope, and deliberately: the upload queue outlives
 * the db-files page, so setups keep running after that page unmounts. A repair
 * pass that ran on the next mount without consulting this would re-dispatch a
 * file whose first dispatch is still in flight, which costs a duplicate indexing
 * run of the whole document.
 */
const activeIndexSetupIds = new Set<string>();

export function beginIndexSetup(id?: string): void {
    if (id) activeIndexSetupIds.add(id);
}
export function endIndexSetup(id?: string): void {
    if (id) activeIndexSetupIds.delete(id);
}
export function isIndexSetupInFlight(id: string): boolean {
    return activeIndexSetupIds.has(id);
}

/** How long one tab's claim on a row's setup is believed. Past it the claim is
 *  assumed dead (the tab was closed mid-setup) and another may take over, which
 *  is the only thing standing between a crashed tab and a file nobody retries. */
export const INDEX_SETUP_LEASE_MS = 5 * 60 * 1000;

/**
 * Claim this row's index setup across TABS.
 *
 * activeIndexSetupIds only knows about this JS context, so two tabs open on the
 * same project would both find the same rows on mount and both dispatch: one
 * document indexed twice, two collapsed rows in the chat, every extracted record
 * saved twice. The row itself is the only state both tabs share, and a single
 * readwrite transaction is atomic across them, so the claim is a compare-and-set
 * on the row.
 *
 * `requireRow` separates the two callers. A REPAIR's mandate is the row: if it
 * is gone, another tab finished the work and there is nothing to do. A LIVE
 * upload owns its work regardless, so a missing row (persistence refused, or the
 * host target which never persists) must not stop it from indexing.
 */
export async function leaseIndexSetup(
    id: string | undefined,
    opts?: { requireRow?: boolean; staleMs?: number },
): Promise<boolean> {
    if (!id) return !opts?.requireRow;
    const db = await openDb();
    // No persistence at all: fall back to the single-tab behaviour rather than
    // refusing to index anything.
    if (!db) return !opts?.requireRow;
    const staleMs = opts?.staleMs ?? INDEX_SETUP_LEASE_MS;
    try {
        return await new Promise<boolean>((resolve, reject) => {
            const tx = db.transaction(STORE, "readwrite");
            const os = tx.objectStore(STORE);
            const req = os.get(id);
            let won = false;
            req.onsuccess = () => {
                const row = req.result as PersistedUpload | undefined;
                if (!row) { won = !opts?.requireRow; return; }
                const held = row.setupLeaseAt || 0;
                if (held && Date.now() - held < staleMs) return; // another tab has it
                won = true;
                os.put(Object.assign({}, row, { setupLeaseAt: Date.now() }));
            };
            tx.oncomplete = () => resolve(won);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } catch (e) {
        // An unreadable store must not be able to stop a file being indexed.
        return !opts?.requireRow;
    }
}

/** Give the claim back, so a retry does not have to wait out the lease. */
export async function releaseIndexSetupLease(id?: string): Promise<void> {
    if (!id) return;
    await updatePersistedUpload(id, { setupLeaseAt: 0 });
}

/**
 * Run `task` over `items` with at most `limit` in flight.
 *
 * The dispatch itself is already serialized downstream (skapi routes queued
 * requests through a per-queue pass with batchSize 1), so this is not about
 * request rate: it is about how many setup chains may be PARKED at once. Firing
 * one per file for a 10k batch parks 10k promise chains and their closures,
 * which is the shape that has run this page out of memory before.
 */
export async function runBounded<T>(
    items: T[],
    limit: number,
    task: (item: T, index: number) => Promise<void>,
): Promise<void> {
    if (!items.length) return;
    let next = 0;
    const workers: Promise<void>[] = [];
    const width = Math.max(1, Math.min(limit, items.length));
    for (let w = 0; w < width; w++) {
        workers.push((async () => {
            while (true) {
                const i = next++;
                if (i >= items.length) return;
                try {
                    await task(items[i], i);
                } catch (e) {
                    // A task owns its own error reporting; one failure must not
                    // strand the rest of the batch.
                }
            }
        })());
    }
    await Promise.all(workers);
}

/**
 * A persisted File is a REFERENCE to bytes on disk (that is what keeps a 10k-file
 * batch from being copied into the origin's storage). If the user moved, renamed
 * or edited the file after dropping it, the reference is stale and reading it
 * throws — so a resume must probe before trusting the handle, and report the ones
 * it cannot read instead of failing them silently mid-batch.
 */
export async function isFileStillReadable(file?: File): Promise<boolean> {
    try {
        // Undefined is the normal state of a row past the upload phase, whose
        // blob was dropped on purpose. Not readable, and not meant to be.
        if (!file || typeof file.slice !== "function") return false;
        await file.slice(0, 1).arrayBuffer();
        return true;
    } catch (e) {
        return false;
    }
}

// ─── keepalive: Web Lock + unload guard ──────────────────────────────────────

// One entry per project with a batch running: its worker refcount and the
// resolver that releases its Web Lock.
const keepalives = new Map<string, { depth: number; release: (() => void) | null }>();
let unloadGuardInstalled = false;

const onBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // Legacy channel; modern browsers show their own wording and ignore this.
    e.returnValue = "";
    return "";
};

/**
 * Mark this page as busy for as long as an upload batch is running.
 *
 * The Web Lock is the load-bearing half: Chromium's freezing policy leaves a page
 * that holds one alone, which is what keeps a batch progressing in a background
 * tab overnight instead of being suspended after ~5 minutes hidden. It is a
 * strong signal, NOT a guarantee — an OS-level kill or a memory-pressure discard
 * ignores it, which is why the queue is also persisted.
 *
 * Reference-counted per KEY (the project a batch is uploading to; "" for the
 * hosting page). A Web Lock is exclusive and the name used to be a single
 * constant, so with two tabs uploading to two different projects only the first
 * one ever held a lock and the second was a freeze candidate from the moment it
 * went to the background — its upload appearing to stall for no reason. One name
 * per project means each of those tabs holds its own.
 */
export function acquireUploadKeepalive(key = ""): void {
    let entry = keepalives.get(key);
    if (!entry) {
        entry = { depth: 0, release: null };
        keepalives.set(key, entry);
    }
    entry.depth++;
    if (entry.depth > 1) return;

    if (typeof window !== "undefined" && !unloadGuardInstalled) {
        window.addEventListener("beforeunload", onBeforeUnload);
        unloadGuardInstalled = true;
    }
    try {
        const locks = (navigator as any)?.locks;
        if (locks?.request) {
            const held = entry;
            locks.request("bq-upload-in-progress" + (key ? ":" + key : ""), () =>
                new Promise<void>((resolve) => {
                    // The batch can have finished while this request was queued
                    // behind another tab's lock on the same project — in which
                    // case release it immediately rather than holding it for a
                    // batch that is over.
                    if (held.depth === 0) resolve();
                    else held.release = resolve;
                })
            ).catch(() => { held.release = null; });
        }
    } catch (e) { /* Web Locks unsupported; the persisted queue still covers us */ }
}

export function releaseUploadKeepalive(key = ""): void {
    const entry = keepalives.get(key);
    if (!entry || entry.depth === 0) return;
    entry.depth--;
    if (entry.depth > 0) return;

    if (entry.release) {
        try { entry.release(); } catch (e) { /* already released */ }
        entry.release = null;
    }
    keepalives.delete(key);

    // The warning covers the page, so it comes off only once NO project is
    // uploading — dropping it on the first batch to finish would leave the
    // others unguarded.
    if (keepalives.size) return;
    if (typeof window !== "undefined" && unloadGuardInstalled) {
        window.removeEventListener("beforeunload", onBeforeUnload);
        unloadGuardInstalled = false;
    }
}
